import { useEffect, useState } from "react";
import LinkIcon from "@mui/icons-material/Link";
import { Box, Popover, TextField, Tooltip, Typography } from "@mui/material";

import { CoAppActionButton, CoAppCancelTextButton, CoAppDestructiveActionButton } from "../../../styled/global.styled";
import MenuButton from "../MenuButton";

const LinkPopover = ({ editor }) => {
	const [linkUrl, setLinkUrl] = useState("");
	const [linkText, setLinkText] = useState("");
	const [anchorEl, setAnchorEl] = useState(null);
	const [isEditing, setIsEditing] = useState(false);
	const [originalUrl, setOriginalUrl] = useState("");
	const [originalText, setOriginalText] = useState("");

	useEffect(() => {
		if (!editor) return;
		
		const handleUpdate = () => {
			try {
				const isLinkActive = editor.isActive("link");
				
				if (isLinkActive) {
					const attrs = editor.getAttributes("link");
					
					if (attrs.href) {
						setLinkUrl(attrs.href);
						setOriginalUrl(attrs.href);
						setIsEditing(true);

						const { from, to } = editor.state.selection;
						const selectedText = editor.state.doc.textBetween(from, to);

						setLinkText(selectedText || "");
						setOriginalText(selectedText || "");
					}
				} else {
					const { from, to } = editor.state.selection;
					const selectedText = editor.state.doc.textBetween(from, to);
					
					setLinkText(selectedText || "");
					setOriginalText(selectedText || "");
					setLinkUrl("");
					setOriginalUrl("");
					setIsEditing(false);
				}
			} catch (error) {
				console.error("Error in selection update handler:", error);
			}
		};

		editor.on("selectionUpdate", handleUpdate);
		
		return () => {
			editor.off("selectionUpdate", handleUpdate);
		};
	}, [editor]);

	const handleOpen = (event) => {
		setAnchorEl(event.currentTarget);
		
		try {
			if (editor && editor.isActive("link")) {
				const attrs = editor.getAttributes("link");
				
				if (attrs.href) {
					setLinkUrl(attrs.href);
					setOriginalUrl(attrs.href);
					setIsEditing(true);

					const { from, to } = editor.state.selection;
					const selectedText = editor.state.doc.textBetween(from, to);
					
					setLinkText(selectedText || "");
					setOriginalText(selectedText || "");
				}
			} else if (editor) {
				const { from, to } = editor.state.selection;
				const selectedText = editor.state.doc.textBetween(from, to);
				
				setLinkText(selectedText || "");
				setOriginalText(selectedText || "");
				setLinkUrl("");
				setOriginalUrl("");
				setIsEditing(false);
			}
		} catch (error) {
			console.error("Error in handleOpen:", error);
		}
	};

	const handleClose = () => {
		setAnchorEl(null);
		setLinkUrl("");
		setLinkText("");
		setOriginalUrl("");
		setOriginalText("");
		setIsEditing(false);
	};

	const addOrUpdateLink = () => {
		if (!editor || !linkUrl) return;
		
		try {
			if (isEditing) {
				editor
					.chain()
					.focus()
					.setLink({ href: linkUrl })
					.run();
			} else {
				const { from, to } = editor.state.selection;
				const selectedText = editor.state.doc.textBetween(from, to);
				
				if (!selectedText && linkText) {
					editor
						.chain()
						.focus()
						.insertContent(linkText)
						.setTextSelection({ from, to: from + linkText.length })
						.setLink({ href: linkUrl })
						.run();
				} else {
					if (selectedText !== linkText) {
						editor
							.chain()
							.focus()
							.deleteRange({ from, to })
							.insertContent(linkText)
							.setTextSelection({ from, to: from + linkText.length })
							.setLink({ href: linkUrl })
							.run();
					} else {
						editor
							.chain()
							.focus()
							.setLink({ href: linkUrl })
							.run();
					}
				}
			}
		} catch (error) {
			console.error("Error in addOrUpdateLink:", error);
		}
		
		handleClose();
	};
	
	const removeLink = () => {
		if (!editor) return;
		
		try {
			editor.chain().focus().unsetLink().run();
		} catch (error) {
			console.error("Error in removeLink:", error);
		}
		
		handleClose();
	};

	const hasChanges = () => {
		return linkUrl !== originalUrl || linkText !== originalText;
	};

	return (
		<>
			<Tooltip title="Insert Link">
				<MenuButton
					isActive={editor?.isActive("link")}
					onClick={handleOpen}
				>
					<LinkIcon fontSize="small" />
				</MenuButton>
			</Tooltip>
			
			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
			>
				<Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1, width: "600px" }}>
					{isEditing && (
						<Typography variant="caption" sx={{ color: "text.secondary" }}>
							Editing existing link
						</Typography>
					)}
					
					<TextField
						label="Link Text"
						size="small"
						value={linkText}
						onChange={(e) => setLinkText(e.target.value)}
						autoFocus
					/>
					
					<TextField
						label="Link URL"
						size="small"
						value={linkUrl}
						onChange={(e) => setLinkUrl(e.target.value)}
						onKeyDown={(e) => e.key === "Enter" && addOrUpdateLink()}
						placeholder="https://example.com"
					/>
					
					<Box sx={{ display: "flex", justifyContent: "space-between" }}>
						<Box>
							<CoAppDestructiveActionButton 
								onClick={removeLink} 
								size="small" 
								sx={{ 
									display: editor?.isActive("link") ? "inline-flex" : "none",
									padding: "4px 12px"
								}}
							>
								Remove Link
							</CoAppDestructiveActionButton>
						</Box>

						<Box sx={{ display: "flex", gap: 1 }}>
							<CoAppCancelTextButton 
								onClick={handleClose} 
								size="small"
							>
								Cancel
							</CoAppCancelTextButton>

							<CoAppActionButton 
								onClick={addOrUpdateLink} 
								disabled={!linkUrl || (isEditing && !hasChanges())} 
								size="small"
								sx={{ padding: "4px 12px" }}
							>
								{isEditing ? "Update" : "Add"}
							</CoAppActionButton>
						</Box>
					</Box>
				</Box>
			</Popover>
		</>
	);
};

export default LinkPopover;